/**
 * HoyPage — Panel operativo de coordinación del día.
 *
 * Rediseño: agrupación por salida (zarpe), KPI bar sticky, toggle Tarjetas/Tabla
 * persistente en localStorage. Foco: operador de recepción del muelle,
 * escritorio y móvil.
 *
 * NO incluye la acción "Embarcar" — ese flujo vive en Muelle. Booking es
 * planificación y coordinación.
 */
import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Stack, ToggleButton, ToggleButtonGroup,
  Chip, useMediaQuery, useTheme,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ReservaSlideOver from './ReservaSlideOver';
import ReservacionesTable from './shared/ReservacionesTable';
import KpiBar from './shared/KpiBar';
import VoyageCard from './shared/VoyageCard';
import BorrarReservaDialog from './shared/BorrarReservaDialog';
import { categorizar } from './shared/useReservaciones';
import { API_BASE, getEmpresaId, todayLocal, norm } from './shared/format';
import { exportarReservasCSV } from './shared/exportCSV';
import { agruparPorSalida } from './shared/agruparPorSalida';

const VIEW_STORAGE_KEY = 'nara.booking.hoy.view';

function loadInitialView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'table' ? 'table' : 'cards';
  } catch { return 'cards'; }
}
function saveView(v) {
  try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* no-op */ }
}

const FILTROS = [
  { id: 'todas', label: 'Todas', predicado: () => true },
  { id: 'por_cobrar', label: 'Por cobrar',
    predicado: r => Number(r.saldo_pendiente || 0) > 0
      && (r.saldo_a_cargo_de || '') !== 'agencia'
      && r.estado !== 'cancelada' && r.estado !== 'completada' },
  { id: 'cxc_agencia', label: 'CxC agencia',
    predicado: r => Number(r.saldo_pendiente || 0) > 0
      && (r.saldo_a_cargo_de || '') === 'agencia'
      && r.estado !== 'cancelada' },
  { id: 'cobrado_muelle', label: 'Cobrado en muelle',
    predicado: r => Number(r.cobrado_muelle || 0) > 0 },
  { id: 'con_agencia', label: 'Con agencia', predicado: r => !!r.agencia_id },
  { id: 'cortesias', label: 'Cortesías',
    predicado: r => (Number(r.cortesias) || 0) > 0 || r.forma_pago_agencia === 'cortesia' },
  { id: 'directas', label: 'Directas', predicado: r => !r.agencia_id },
];

export default function HoyPage() {
  const { reservaciones, isLoading, refetch, horaCutoff } = useOutletContext();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [detalleId, setDetalleId] = useState(null);
  const [borrarReserva, setBorrarReserva] = useState(null);
  const [fechaManifiesto, setFechaManifiesto] = useState(todayLocal());
  const [view, setView] = useState(loadInitialView);
  const [busqueda, setBusqueda] = useState('');
  const [filtroId, setFiltroId] = useState('todas');

  const { hoy } = categorizar(reservaciones);

  // Cuentas por chip (con la búsqueda aplicada, sin el filtro activo — así
  // los chips muestran cuántas quedan de cada tipo *dentro* del set buscado).
  const filtradasPorBusqueda = useMemo(() => {
    const q = norm(busqueda.trim());
    if (!q) return hoy;
    return hoy.filter(r => (
      norm(r.codigo_reserva).includes(q) ||
      norm(r.nombre_cliente).includes(q) ||
      norm(r.numero_whatsapp).includes(q) ||
      norm(r.correo).includes(q) ||
      norm(r.agencia_nombre).includes(q) ||
      norm(r.paquete_nombre).includes(q)
    ));
  }, [hoy, busqueda]);

  const counts = useMemo(() => {
    const out = {};
    FILTROS.forEach(f => { out[f.id] = filtradasPorBusqueda.filter(f.predicado).length; });
    return out;
  }, [filtradasPorBusqueda]);

  const rowsFinal = useMemo(() => {
    const filtro = FILTROS.find(f => f.id === filtroId) || FILTROS[0];
    return filtradasPorBusqueda.filter(filtro.predicado);
  }, [filtradasPorBusqueda, filtroId]);

  const { grupos, sinSalida } = useMemo(() => agruparPorSalida(rowsFinal), [rowsFinal]);

  const handleView = (_e, v) => {
    if (!v) return;
    setView(v);
    saveView(v);
  };

  const descargarManifiesto = () => {
    const url = `${API_BASE}/turismo/manifiesto-dimar?empresa_id=${getEmpresaId()}&fecha=${fechaManifiesto}`;
    window.open(url, '_blank');
  };
  const descargarCSV = () => exportarReservasCSV(rowsFinal, `reservas-hoy-${todayLocal()}.csv`);

  return (
    <Box>
      {/* Barra superior sticky con título + KPIs + acciones */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 5,
        bgcolor: '#fff',
        py: 1.5, mb: 1.5,
        borderBottom: '1px solid #e2e8f0',
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1.5, mb: 1.5,
        }}>
          <Box>
            <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
              Reservas de hoy
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              {hoy.length} {hoy.length === 1 ? 'reserva' : 'reservas'}
              {grupos.length > 0 && ` en ${grupos.length} ${grupos.length === 1 ? 'salida' : 'salidas'}`}
              {' · '}{todayLocal()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {isSmUp && (
              <TextField size="small" type="date" label="Fecha manifiesto"
                value={fechaManifiesto}
                onChange={e => setFechaManifiesto(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { sm: 158 } }} />
            )}
            <Button variant="outlined" size="small"
              startIcon={isSmUp ? <DownloadIcon /> : null}
              onClick={descargarCSV}
              disabled={rowsFinal.length === 0}
              sx={{
                textTransform: 'none', fontWeight: 600,
                color: '#0f766e', borderColor: '#0f766e',
                minWidth: { xs: 40, sm: 'auto' },
                px: { xs: 1, sm: 1.5 },
              }}
              aria-label="Descargar CSV">
              {isSmUp ? `CSV (${rowsFinal.length})` : <DownloadIcon sx={{ fontSize: 18 }} />}
            </Button>
            <Button variant="contained" size="small"
              startIcon={isSmUp ? <PictureAsPdfIcon /> : null}
              onClick={descargarManifiesto}
              sx={{
                textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                boxShadow: '0 4px 12px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)' },
                minWidth: { xs: 40, sm: 'auto' },
                px: { xs: 1, sm: 1.5 },
              }}
              aria-label="Manifiesto DIMAR">
              {isSmUp
                ? (isMdUp ? 'Manifiesto DIMAR' : 'Manifiesto')
                : <PictureAsPdfIcon sx={{ fontSize: 18 }} />}
            </Button>
          </Stack>
        </Box>

        <KpiBar rows={hoy} activeFilter={filtroId} onFilter={setFiltroId} />
      </Box>

      {/* Toolbar: toggle + filtros.
          La búsqueda local se eliminó porque duplica visualmente la barra
          global (BusquedaGlobal en BookingLayout) que ya cubre búsqueda por
          texto en TODAS las secciones. Los chips de filtros de abajo cubren
          el filtrado por categoría (Sin embarque, Sin pagar, CxC, etc.). */}
      <Box sx={{
        display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
        mb: 1.5,
      }}>
        <ToggleButtonGroup
          size="small" value={view} exclusive onChange={handleView}
          sx={{
            bgcolor: '#f8fafc',
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: 12,
              px: 1.25, py: 0.6, color: '#475569',
              border: '1px solid #e2e8f0',
              '&.Mui-selected': {
                bgcolor: '#fff', color: '#0f172a',
                boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="cards"><ViewModuleIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tarjetas</ToggleButton>
          <ToggleButton value="table"><ViewListIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tabla</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{
          display: 'flex', gap: 0.5, overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          {FILTROS.map(f => {
            const active = f.id === filtroId;
            return (
              <Chip key={f.id} size="small"
                clickable
                label={
                  <span>
                    {f.label}
                    <Box component="span" sx={{
                      ml: 0.6, color: active ? 'rgba(255,255,255,0.85)' : '#94a3b8',
                      fontWeight: 500,
                    }}>
                      {counts[f.id]}
                    </Box>
                  </span>
                }
                onClick={() => setFiltroId(f.id)}
                sx={{
                  bgcolor: active ? '#0891b2' : '#f8fafc',
                  color: active ? '#fff' : '#475569',
                  border: `1px solid ${active ? '#0e7490' : '#e2e8f0'}`,
                  fontWeight: 500,
                  '&:hover': { bgcolor: active ? '#0e7490' : '#eef2f6' },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Contenido: agrupado por salida (default) */}
      {rowsFinal.length === 0 ? (
        <Box sx={{
          p: 5, textAlign: 'center', bgcolor: '#f8fafc',
          border: '1px dashed #cbd5e1', borderRadius: 2,
        }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            {isLoading ? 'Cargando…' : 'No hay reservas'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            {busqueda
              ? `No hay coincidencias con "${busqueda}".`
              : filtroId !== 'todas'
                ? 'No hay reservas en este filtro.'
                : 'Cuando llegue una reserva con salida hoy, aparecerá aquí.'}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {grupos.map(g => (
            <VoyageCard
              key={g.salida_id}
              hora={g.hora}
              paquete={g.paquete}
              lanchas={g.lanchas}
              capacidad={g.capacidad}
              ocupados={g.ocupados}
              reservas={g.reservas}
              view={view}
              onRowClick={r => setDetalleId(r.id)}
              onDelete={r => setBorrarReserva(r)}
              horaCutoff={horaCutoff}
            />
          ))}
          {sinSalida.length > 0 && (
            <Box sx={{
              bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5,
            }}>
              <Typography sx={{
                fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                fontWeight: 700, color: '#64748b', mb: 1,
              }}>
                Sin salida asignada · {sinSalida.length}
              </Typography>
              <ReservacionesTable
                rows={sinSalida} loading={false}
                hideSearch                       /* la barra global de arriba ya filtra este subset */
                onRowClick={r => setDetalleId(r.id)}
                onDelete={r => setBorrarReserva(r)}
                horaCutoff={horaCutoff}
                showFecha={false}
                emptyTitle="" emptyMessage=""
              />
            </Box>
          )}
        </Stack>
      )}

      {detalleId && (
        <ReservaSlideOver
          reservaId={detalleId}
          onClose={() => setDetalleId(null)}
          onUpdated={refetch}
        />
      )}

      <BorrarReservaDialog
        reserva={borrarReserva}
        open={!!borrarReserva}
        onClose={() => setBorrarReserva(null)}
        onDeleted={() => { setBorrarReserva(null); refetch(); }}
      />
    </Box>
  );
}
